import draftToHtml from 'draftjs-to-html';
import { backHomeLink } from 'utils/constants';
import { ParagraphEnum } from '../constants';
import pLimit from 'p-limit';

const limit = pLimit(1);

const sharedInputAttributes = (locale) => ({
  status: true,
  default_langcode: locale === 'it',
  revision_translation_affected: true,
  content_translation_source: 'und',
  content_translation_outdated: false,
});
const sharedReq = {
  action: 'create',
  headers: {
    Accept: 'application/vnd.api+json',
    'Content-Type': 'application/vnd.api+json',
  },
};
const getReq = (name, input, locale, position) => ({
  ...sharedReq,
  requestId: `req-${position}`,
  uri: `${backHomeLink}/${locale}/jsonapi/paragraph/${name}`,
  body: input,
});
const getInput = (name, otherAttributes, relationships = false, locale) =>
  JSON.stringify({
    data: {
      type: `paragraph--${name}`,
      attributes: {
        ...sharedInputAttributes(locale),
        ...otherAttributes,
      },
      ...(relationships &&
        Object.keys(relationships)?.length && {
          relationships: relationships,
        }),
    },
  });

const sharedEditReq = {
  action: 'update',
  headers: {
    Accept: 'application/vnd.api+json',
    'Content-Type': 'application/vnd.api+json',
  },
};

const getEditReq = (name, uid, input, locale, position) => ({
  ...sharedEditReq,
  requestId: `req-${position}`,
  uri: `${backHomeLink}/${locale}/jsonapi/paragraph/${name}/${uid}`,
  body: input,
});

const getEditInput = (
  name,
  uid,
  otherAttributes,
  relationships = false,
  locale
) =>
  JSON.stringify({
    data: {
      type: `paragraph--${name}`,
      id: uid,
      'set-new-revision': true,
      attributes: {
        ...sharedInputAttributes(locale),
        ...otherAttributes,
      },
      ...(relationships &&
        Object.keys(relationships)?.length && {
          relationships: relationships,
        }),
    },
  });

const submitTextOrRichText = (comp, value) =>
  comp.format
    ? {
        value:
          typeof value === 'string'
            ? draftToHtml(JSON.parse(value))
            : draftToHtml(value),
        format: comp.format,
      }
    : value;

/**
 * YOUTUBE VIDEO Create/Edit
 */

export const createYoutubeVideo = ({
  submittedData,
  additionalComponents,
  bluePrintArray,
  locale,
  position,
}) => {
  const richTextComp = additionalComponents.find(
    (comp) => comp.name === 'field_didascalia'
  );
  const videoSrc = submittedData.field_url;
  const caption = submittedData.field_didascalia
    ? submitTextOrRichText(richTextComp, submittedData.field_didascalia)
    : undefined;
  if (videoSrc || caption) {
    const input = getInput(
      submittedData.name,
      {
        ...(videoSrc && {
          field_url: videoSrc,
        }),
        ...(caption && {
          field_didascalia: caption,
        }),
      },
      false,
      locale
    );
    const req = getReq(submittedData.name, input, locale, position);
    bluePrintArray.push(req);
  }
};

export const editYoutubeVideo = ({
  submittedData,
  additionalComponents,
  entityId,
  bluePrintArray,
  locale,
  position,
}) => {
  const richTextComp = additionalComponents.find(
    (comp) => comp.name === 'field_didascalia'
  );
  const videoSrc = submittedData.field_url || '';
  const caption = submittedData.field_didascalia
    ? submitTextOrRichText(richTextComp, submittedData.field_didascalia)
    : '';
  const input = getEditInput(
    submittedData.name,
    entityId,
    {
      ...(videoSrc && {
        field_url: videoSrc,
      }),
      ...(caption && {
        field_didascalia: caption,
      }),
    },
    false,
    locale
  );
  const req =
    input && getEditReq(submittedData.name, entityId, input, locale, position);
  req && bluePrintArray.push(req);
};

/**
 * VIMEO VIDEO Create/Edit
 */

export const createVimeoVideo = ({
  submittedData,
  bluePrintArray,
  locale,
  position,
}) => {
  const val = submittedData.field_vimeo_link;
  if (val) {
    const input = getInput(
      submittedData.name,
      {
        field_vimeo_link: val,
      },
      false,
      locale
    );
    const req = getReq(submittedData.name, input, locale, position);
    bluePrintArray.push(req);
  }
};

export const editVimeoVideo = ({
  submittedData,
  entityId,
  bluePrintArray,
  locale,
  position,
}) => {
  const val = submittedData.field_vimeo_link;
  if (val) {
    const input = getEditInput(
      submittedData.name,
      entityId,
      {
        field_vimeo_link: val,
      },
      false,
      locale
    );
    const req =
      input &&
      getEditReq(submittedData.name, entityId, input, locale, position);
    req && bluePrintArray.push(req);
  }
};

/**
 * SD BRAND MULTITEXT Create/Edit
 */

export const createSdBrandMultitext = ({
  submittedData,
  additionalComponents,
  bluePrintArray,
  locale,
  position,
}) => {
  const richTextComp = additionalComponents.find(
    (comp) => comp.name === 'field_paragraph_simple_text'
  );
  const val = submittedData.field_paragraph_simple_text;
  if (val) {
    const input = getInput(
      submittedData.name,
      {
        field_paragraph_simple_text: submitTextOrRichText(richTextComp, val),
      },
      false,
      locale
    );
    const req = getReq(submittedData.name, input, locale, position);
    bluePrintArray.push(req);
  }
};

export const editSdBrandMultitext = ({
  submittedData,
  additionalComponents,
  entityId,
  bluePrintArray,
  locale,
  position,
}) => {
  const richTextComp = additionalComponents.find(
    (comp) => comp.name === 'field_paragraph_simple_text'
  );
  const input = getEditInput(
    submittedData.name,
    entityId,
    {
      field_paragraph_simple_text: submitTextOrRichText(
        richTextComp,
        submittedData.field_paragraph_simple_text
      ),
    },
    false,
    locale
  );
  const req =
    input && getEditReq(submittedData.name, entityId, input, locale, position);
  req && bluePrintArray.push(req);
};

/**
 * QUOTE Create/Edit
 */

export const createQuotedText = ({
  submittedData,
  bluePrintArray,
  locale,
  position,
}) => {
  const val = submittedData.field_testo_semplice;
  if (val) {
    const input = getInput(
      submittedData.name,
      {
        field_testo_semplice: val,
      },
      false,
      locale
    );
    const req = getReq(submittedData.name, input, locale, position);
    bluePrintArray.push(req);
  }
};

export const editQuotedText = ({
  submittedData,
  entityId,
  bluePrintArray,
  locale,
  position,
}) => {
  // Lo aggiungo ai BluePrint da Creare
  const input = getEditInput(
    submittedData.name,
    entityId,
    {
      field_testo_semplice: submittedData.field_testo_semplice,
    },
    false,
    locale
  );
  const req =
    input && getEditReq(submittedData.name, entityId, input, locale, position);
  req && bluePrintArray.push(req);
};

/**
 * TITLE Create/Edit
 */

export const createTitleSection = ({
  submittedData,
  bluePrintArray,
  locale,
  position,
}) => {
  const val = submittedData.field_section_title;
  if (val) {
    const input = getInput(
      submittedData.name,
      {
        field_section_title: val,
      },
      false,
      locale
    );
    const req = getReq(submittedData.name, input, locale, position);
    bluePrintArray.push(req);
  }
};

export const editTitleSection = ({
  submittedData,
  entityId,
  bluePrintArray,
  locale,
  position,
}) => {
  // Lo aggiungo ai BluePrint da Creare
  const input = getEditInput(
    submittedData.name,
    entityId,
    {
      field_section_title: submittedData.field_section_title,
    },
    false,
    locale
  );
  const req =
    input && getEditReq(submittedData.name, entityId, input, locale, position);
  req && bluePrintArray.push(req);
};

/**
 * VIRTUAL SHOWROOM Create/Edit
 */

export const createVirtualShowroom = async ({
  submittedData,
  bluePrintArray,
  locale,
  position,
  createEntityFn,
}) => {
  const src = submittedData.field_link_vs || undefined;
  const title = submittedData.field_titolo_vs || undefined;
  const img = submittedData.field_immagine_vs?.[0] || undefined;
  const alt = img
    ? submittedData?.alt_field_immagine_vs_0 || img.name
    : undefined;
  const _copy = img && new File([img], img.name);
  if (_copy?.preview) delete _copy.preview;
  const relationshipInput = {};
  if (_copy) {
    const res = await createEntityFn({
      langCode: locale,
      customHeaders: {
        'Content-Type': 'application/octet-stream',
        Accept: 'application/vnd.api+json',
        'Content-Disposition': `file; filename="${_copy.name}"`,
      },
      body: _copy,
      fieldName: 'field_immagine_vs',
      entity: 'paragraph',
      bundle: ParagraphEnum.VIRTUAL_SHOWROOM,
      useAxios: false,
    });
    if (res) {
      const imageId = res.id;
      relationshipInput.field_immagine_vs = {
        data: {
          type: 'file--file',
          id: imageId,
          meta: {
            alt: alt || img.name,
          },
        },
      };
    }
  }
  if (src || title || img) {
    const input = getInput(
      submittedData.name,
      {
        ...(src && {
          field_link_vs: src,
        }),
        ...(title && {
          field_titolo_vs: title,
        }),
      },
      { ...relationshipInput },
      locale
    );
    const req = getReq(submittedData.name, input, locale, position);
    bluePrintArray.push(req);
  }
};

export const editVirtualShowroom = async ({
  submittedData,
  entityId,
  bluePrintArray,
  locale,
  position,
  oldData,
  createEntityFn,
}) => {
  const src = submittedData.field_link_vs || undefined;
  const title = submittedData.field_titolo_vs || undefined;

  const oldComponent = oldData.find(
    (c) =>
      c.name === submittedData.name &&
      c?.revisionId === submittedData?.revisionId
  );
  const oldImg = oldComponent?.field_immagine_vs?.[0];
  const newImg = submittedData?.field_immagine_vs?.[0];
  const isImgEdit =
    !oldImg ||
    oldImg.name !== newImg.name ||
    oldImg.size !== newImg.size ||
    oldImg.type !== newImg.type;
  const oldAlt = oldComponent?.alt_field_immagine_vs_0;
  const newAlt = submittedData?.alt_field_immagine_vs_0;
  const isAltEdit = oldAlt !== newAlt;
  const relationshipInput = {};

  if (isImgEdit) {
    const img = submittedData.field_immagine_vs?.[0] || undefined;
    const alt = img
      ? submittedData?.alt_field_immagine_vs_0 || img.name
      : undefined;
    const _copy = img && new File([img], img.name);
    if (_copy?.preview) delete _copy.preview;
    if (_copy) {
      const res = await createEntityFn({
        langCode: locale,
        customHeaders: {
          'Content-Type': 'application/octet-stream',
          Accept: 'application/vnd.api+json',
          'Content-Disposition': `file; filename="${_copy.name}"`,
        },
        body: _copy,
        fieldName: 'field_immagine_vs',
        entity: 'paragraph',
        bundle: ParagraphEnum.VIRTUAL_SHOWROOM,
        useAxios: false,
      });
      if (res) {
        const imageId = res.id;
        relationshipInput.field_immagine_vs = {
          data: {
            type: 'file--file',
            id: imageId,
            meta: {
              alt: alt || img.name,
            },
          },
        };
      }
    }
  }

  if (!isImgEdit && isAltEdit) {
    const imgId = oldComponent?.oldImgId;
    relationshipInput.field_immagine_vs = {
      data: {
        type: 'file--file',
        id: imgId,
        meta: {
          alt: newAlt || oldAlt,
        },
      },
    };
  }

  if (relationshipInput.field_immagine_vs || src || title) {
    const input = getEditInput(
      submittedData.name,
      entityId,
      {
        ...(src && {
          field_link_vs: src,
        }),
        ...(title && {
          field_titolo_vs: title,
        }),
      },
      { ...relationshipInput },
      locale
    );
    const req =
      input &&
      getEditReq(submittedData.name, entityId, input, locale, position);
    req && bluePrintArray.push(req);
  }
};

/**
 * DESIGNER Create/Edit
 */

export const createDesigner = async ({
  submittedData,
  createEntityFn,
  bluePrintArray,
  locale,
  position,
}) => {
  const name = submittedData.field_desiger_name || undefined;
  const img = submittedData.field_image?.[0] || undefined;
  const alt = img ? submittedData?.alt_field_image_0 || img.name : undefined;
  const _copy = img && new File([img], img.name);
  if (_copy?.preview) delete _copy.preview;
  const relationshipInput = {};
  if (_copy) {
    const res = await createEntityFn({
      langCode: locale,
      customHeaders: {
        'Content-Type': 'application/octet-stream',
        Accept: 'application/vnd.api+json',
        'Content-Disposition': `file; filename="${_copy.name}"`,
      },
      body: _copy,
      fieldName: 'field_image',
      entity: 'paragraph',
      bundle: ParagraphEnum.DESIGNER,
      useAxios: false,
    });
    if (res) {
      const imageId = res.id;
      relationshipInput.field_image = {
        data: {
          type: 'file--file',
          id: imageId,
          meta: {
            alt: alt || img.name,
          },
        },
      };
    }
  }
  const input =
    name || img
      ? getInput(
          ParagraphEnum.DESIGNER,
          {
            ...(name && {
              field_desiger_name: name,
            }),
          },
          { ...relationshipInput },
          locale
        )
      : undefined;
  const req = input
    ? getReq(ParagraphEnum.DESIGNER, input, locale, position)
    : undefined;
  req && bluePrintArray.push(req);
};

export const editDesigner = async ({
  submittedData,
  oldData,
  createEntityFn,
  entityId,
  bluePrintArray,
  locale,
  position,
}) => {
  const oldComponent = oldData.find(
    (c) =>
      c.name === submittedData.name &&
      c?.revisionId === submittedData?.revisionId
  );
  const oldImg = oldComponent?.field_image?.[0];
  const newImg = submittedData?.field_image?.[0];
  const isImgEdit =
    !oldImg ||
    oldImg.name !== newImg.name ||
    oldImg.size !== newImg.size ||
    oldImg.type !== newImg.type;
  const oldAlt = oldComponent?.alt_field_image_0;
  const newAlt = submittedData?.alt_field_image_0;
  const isAltEdit = oldAlt !== newAlt;
  const oldName = oldComponent?.field_desiger_name;
  const newName = submittedData?.field_desiger_name;
  const isNameEdit = oldName !== newName;
  const relationshipInput = {};
  if (isImgEdit) {
    const img = submittedData.field_image?.[0] || undefined;
    const alt = img ? submittedData?.alt_field_image_0 || img.name : undefined;
    const _copy = img && new File([img], img.name);
    if (_copy?.preview) delete _copy.preview;
    if (_copy) {
      const res = await createEntityFn({
        langCode: locale,
        customHeaders: {
          'Content-Type': 'application/octet-stream',
          Accept: 'application/vnd.api+json',
          'Content-Disposition': `file; filename="${_copy.name}"`,
        },
        body: _copy,
        fieldName: 'field_image',
        entity: 'paragraph',
        bundle: ParagraphEnum.DESIGNER,
        useAxios: false,
      });
      if (res) {
        const imageId = res.id;
        relationshipInput.field_image = {
          data: {
            type: 'file--file',
            id: imageId,
            meta: {
              alt: alt || img.name,
            },
          },
        };
      }
    }
  }

  if (!isImgEdit && isAltEdit) {
    const imgId = oldComponent?.oldImgId;
    relationshipInput.field_image = {
      data: {
        type: 'file--file',
        id: imgId,
        meta: {
          alt: newAlt || oldAlt,
        },
      },
    };
  }

  if (relationshipInput.field_image || isNameEdit) {
    const input = getEditInput(
      submittedData.name,
      entityId,
      {
        ...((oldName || newName) && {
          field_desiger_name: isNameEdit ? newName : oldName,
        }),
      },
      { ...relationshipInput },
      locale
    );
    const req =
      input &&
      getEditReq(submittedData.name, entityId, input, locale, position);
    req && bluePrintArray.push(req);
  }
};

/**
 * FREE IMAGE Create/Edit
 */

export const createFreeImage = async ({
  submittedData,
  additionalComponents,
  createEntityFn,
  bluePrintArray,
  locale,
  position,
}) => {
  const richTextComp = additionalComponents.find(
    (comp) => comp.name === 'field_caption'
  );
  const caption = submittedData.field_caption
    ? submitTextOrRichText(richTextComp, submittedData.field_caption)
    : undefined;
  const img = submittedData.field_immagine?.[0] || undefined;
  const alt = img ? submittedData?.alt_field_immagine_0 || img.name : undefined;
  const _copy = img && new File([img], img.name);
  if (_copy?.preview) delete _copy.preview;
  const relationshipInput = {};
  if (_copy) {
    const res = await createEntityFn({
      langCode: locale,
      customHeaders: {
        'Content-Type': 'application/octet-stream',
        Accept: 'application/vnd.api+json',
        'Content-Disposition': `file; filename="${_copy.name}"`,
      },
      body: _copy,
      fieldName: 'field_immagine',
      entity: 'paragraph',
      bundle: ParagraphEnum.FREE_IMAGE,
      useAxios: false,
    });
    if (res) {
      const imageId = res.id;
      relationshipInput.field_immagine = {
        data: {
          type: 'file--file',
          id: imageId,
          meta: {
            alt: alt || img.name,
          },
        },
      };
    }
    const input =
      caption || img
        ? getInput(
            ParagraphEnum.FREE_IMAGE,
            {
              ...(caption && {
                field_caption: caption,
              }),
            },
            { ...relationshipInput },
            locale
          )
        : undefined;
    const req = input
      ? getReq(ParagraphEnum.FREE_IMAGE, input, locale, position)
      : undefined;
    req && bluePrintArray.push(req);
  }
};

export const editFreeImage = async ({
  submittedData,
  oldData,
  additionalComponents,
  createEntityFn,
  entityId,
  bluePrintArray,
  locale,
  position,
}) => {
  const richTextComp = additionalComponents.find(
    (comp) => comp.name === 'field_caption'
  );
  const oldComponent = oldData.find(
    (c) =>
      c.name === submittedData.name &&
      c?.revisionId === submittedData?.revisionId
  );
  const oldImg = oldComponent?.field_immagine?.[0];
  const newImg = submittedData?.field_immagine?.[0];
  const isImgEdit =
    !oldImg ||
    oldImg.name !== newImg.name ||
    oldImg.size !== newImg.size ||
    oldImg.type !== newImg.type;
  const oldAlt = oldComponent?.alt_field_immagine_0;
  const newAlt = submittedData?.alt_field_immagine_0;
  const isAltEdit = oldAlt !== newAlt;
  const oldCaption = oldComponent?.field_caption;
  const newCaption = submittedData?.field_caption;
  const isCaptionEdit = oldCaption !== newCaption;
  const relationshipInput = {};
  // se l'immagine è stata editata devo crearne una nuova prima
  if (isImgEdit) {
    const img = submittedData.field_immagine?.[0] || undefined;
    const alt = img
      ? submittedData?.alt_field_immagine_0 || img.name
      : undefined;
    const _copy = img && new File([img], img.name);
    if (_copy?.preview) delete _copy.preview;
    if (_copy) {
      const res = await createEntityFn({
        langCode: locale,
        customHeaders: {
          'Content-Type': 'application/octet-stream',
          Accept: 'application/vnd.api+json',
          'Content-Disposition': `file; filename="${_copy.name}"`,
        },
        body: _copy,
        fieldName: 'field_immagine',
        entity: 'paragraph',
        bundle: ParagraphEnum.FREE_IMAGE,
        useAxios: false,
      });
      if (res) {
        const imageId = res.id;
        relationshipInput.field_immagine = {
          data: {
            type: 'file--file',
            id: imageId,
            meta: {
              alt: alt || img.name,
            },
          },
        };
      }
    }
  }

  if (!isImgEdit && isAltEdit) {
    const imgId = oldComponent?.oldImgId;
    relationshipInput.field_immagine = {
      data: {
        type: 'file--file',
        id: imgId,
        meta: {
          alt: newAlt || oldAlt,
        },
      },
    };
  }

  const caption =
    oldCaption || newCaption
      ? submitTextOrRichText(
          richTextComp,
          isCaptionEdit ? newCaption : oldCaption
        )
      : undefined;

  if (relationshipInput.field_immagine || caption) {
    const input = getEditInput(
      submittedData.name,
      entityId,
      {
        ...(caption && {
          field_caption: caption,
        }),
      },
      { ...relationshipInput },
      locale
    );
    const req =
      input &&
      getEditReq(submittedData.name, entityId, input, locale, position);
    req && bluePrintArray.push(req);
  }
};

/**
 * SLIDER Create/Edit
 */
export const createSlider = async ({
  submittedData,
  additionalComponents,
  createEntityFn,
  bluePrintArray,
  locale,
  position,
}) => {
  const sliderPromises = [];
  submittedData?.bp_image_field?.map((f) => {
    const _copy = f && new File([f], f.name);
    if (_copy?.preview) delete _copy.preview;
    if (_copy) {
      sliderPromises.push(
        limit(() =>
          createEntityFn({
            langCode: locale,
            customHeaders: {
              'Content-Type': 'application/octet-stream',
              Accept: 'application/vnd.api+json',
              'Content-Disposition': `file; filename="${_copy.name}"`,
            },
            body: _copy,
            fieldName: 'bp_image_field',
            entity: 'paragraph',
            bundle: 'bp_image',
            useAxios: false,
          })
        )
      );
    }
  });
  if (sliderPromises.length) {
    const paragraphImgPromises = [];
    const res = await Promise.all(sliderPromises);
    res.map((r, idx) => {
      const imageId = r.id;
      const foundImgData = {
        alt: submittedData?.fileAlts?.[idx] || '',
        caption: submittedData?.fileCaptions?.[idx] || '',
      };

      const richTextComp = additionalComponents.find(
        (comp) =>
          comp.name === 'field_caption' || comp.name === 'field_didascalia'
      );
      const caption = foundImgData?.caption
        ? submitTextOrRichText(richTextComp, foundImgData.caption)
        : undefined;
      const alt =
        foundImgData?.alt ||
        submittedData?.bp_image_field[idx]?.name ||
        imageId;
      const basicInput = {
        data: {
          type: 'paragraph--bp_image',
          attributes: {
            status: true,
            default_langcode: true,
            revision_translation_affected: true,
            content_translation_source: 'und',
            content_translation_outdated: false,
            ...(caption && {
              field_caption: caption,
            }),
          },
          relationships: {
            bp_image_field: {
              data: {
                type: 'file--file',
                id: imageId,
                meta: {
                  alt: alt,
                },
              },
            },
          },
        },
      };
      paragraphImgPromises.push(
        limit(() =>
          createEntityFn({
            useLoader: false,
            langCode: locale,
            body: basicInput,
            entity: 'paragraph',
            bundle: 'bp_image',
          })
        )
      );
    });

    if (paragraphImgPromises.length) {
      const resData = await Promise.all(paragraphImgPromises);
      const paragraphData = [];
      resData.map((r) => {
        const paragraphId = r.data?.id;
        paragraphId &&
          paragraphData.push({
            type: 'paragraph--bp_image',
            id: paragraphId,
            meta: {
              target_revision_id:
                r.data.attributes.drupal_internal__revision_id,
            },
          });
      });
      const input = paragraphData?.length
        ? getInput(
            ParagraphEnum.SLIDER_DETAIL,
            {},
            {
              field_slider_detail: {
                data: paragraphData,
              },
            },
            locale
          )
        : undefined;
      const req = input
        ? getReq(ParagraphEnum.SLIDER_DETAIL, input, locale, position)
        : undefined;
      req && bluePrintArray.push(req);
    }
  }
};

export const editSlider = async ({
  submittedData,
  oldData,
  additionalComponents,
  createEntityFn,
  editEntityFn,
  entityId,
  bluePrintArray,
  locale,
  position,
  // otherRevisionsId,
}) => {
  const imgPromises = [];
  const oldComponent = oldData.find(
    (c) =>
      c.name === submittedData.name &&
      c?.revisionId === submittedData?.revisionId
  );
  const positions = submittedData.filePositions;
  submittedData.bp_image_field?.map((f) => {
    // Se il file è diverso da quello salvato prima, lo creiamo nuovo, con tutti i crismi del caso
    if (
      !!f.path ||
      !oldComponent?.bp_image_field?.find(
        (file) => file?.preview === f?.preview
      )
    ) {
      const _copy = f && new File([f], f.name);
      if (_copy?.preview) delete _copy.preview;
      if (_copy) {
        imgPromises.push(
          limit(() =>
            createEntityFn({
              langCode: locale,
              customHeaders: {
                'Content-Type': 'application/octet-stream',
                Accept: 'application/vnd.api+json',
                'Content-Disposition': `file; filename="${_copy.name}"`,
              },
              body: _copy,
              fieldName: 'bp_image_field',
              entity: 'paragraph',
              bundle: 'bp_image',
              useAxios: false,
            })
          )
        );
      }
    } else {
      imgPromises.push(
        limit(
          () =>
            new Promise((resolve) => {
              resolve({ id: 0 });
            })
        )
      );
    }
  });

  if (imgPromises.length) {
    const paragraphPromises = [];
    const res = await Promise.all(imgPromises);
    res.map((r, idx) => {
      const imageId = r.id;
      const richTextComp = additionalComponents.find(
        (comp) =>
          comp.name === 'field_caption' || comp.name === 'field_didascalia'
      );
      if (typeof imageId === 'string') {
        // l'immagine è effetivamente nuova, devo creare anche il paragrafo annesso
        const foundImgData = {
          alt: submittedData?.fileAlts?.[idx] || '',
          caption: submittedData?.fileCaptions?.[idx] || '',
        };
        const caption = foundImgData?.caption
          ? submitTextOrRichText(richTextComp, foundImgData.caption)
          : undefined;
        const alt =
          foundImgData?.alt ||
          submittedData?.bp_image_field[idx]?.name ||
          imageId;

        const basicInput = {
          data: {
            type: 'paragraph--bp_image',
            attributes: {
              status: true,
              default_langcode: true,
              revision_translation_affected: true,
              content_translation_source: 'und',
              content_translation_outdated: false,
              ...(caption && {
                field_caption: caption,
              }),
            },
            relationships: {
              bp_image_field: {
                data: {
                  type: 'file--file',
                  id: imageId,
                  meta: {
                    alt: alt,
                  },
                },
              },
            },
          },
        };

        paragraphPromises.push(
          limit(() =>
            createEntityFn({
              useLoader: false,
              langCode: locale,
              body: basicInput,
              entity: 'paragraph',
              bundle: 'bp_image',
            })
          )
        );
      } else {
        // l'immagine non è stata modificata, faccio un check su alt e caption, se sono stati modificati devo cambiare il rispettivo paragraph
        const newAlt = submittedData.fileAlts?.[idx];
        const oldAlt = oldComponent?.fileAlts?.[idx];
        const isAltEdit = newAlt !== oldAlt;
        const newCaption = submittedData.fileCaptions?.[idx];
        const oldCaption = oldComponent?.fileCaptions?.[idx];
        const isCaptionEdit = newCaption !== oldCaption;
        if (isAltEdit || isCaptionEdit) {
          // editEntity
          const caption =
            isCaptionEdit && newCaption
              ? submitTextOrRichText(richTextComp, newCaption)
              : '';
          const alt = isAltEdit ? newAlt : oldAlt || 'img';
          const effectiveImgPosition = positions[idx];
          const basicInput = {
            data: {
              type: 'paragraph--bp_image',
              id: oldComponent?.oldParagraphId[idx],
              'set-new-revision': true,
              attributes: {
                ...(isCaptionEdit && {
                  field_caption: caption,
                }),
              },
              relationships: {
                bp_image_field: {
                  data: {
                    type: 'file--file',
                    id: oldComponent?.oldImgId[effectiveImgPosition],
                    meta: {
                      alt: alt,
                    },
                  },
                },
              },
            },
          };
          paragraphPromises.push(
            limit(() =>
              editEntityFn({
                useLoader: false,
                langCode: locale,
                body: basicInput,
                entity: 'paragraph',
                bundle: 'bp_image',
                entityId: oldComponent?.oldParagraphId[idx],
              })
            )
          );
        } else {
          // non è cambiata l'immagine, neanche alt ne caption, rimane tutto invariato
          paragraphPromises.push(
            limit(
              () =>
                new Promise((resolve) => {
                  resolve({
                    data: {
                      id: oldComponent?.oldParagraphId[idx],
                      attributes: {
                        drupal_internal__revision_id:
                          oldComponent?.oldRevisionId[idx],
                      },
                    },
                  });
                })
            )
          );
        }
      }
    });

    if (paragraphPromises.length) {
      const resData = await Promise.all(paragraphPromises);
      const paragraphData = [];
      resData.map((r, index) => {
        const paragraphId = r.data?.id;
        const paragraphPosition = positions.indexOf(index);
        const currentRevisionId =
          r.data.attributes.drupal_internal__revision_id;
        // const targetRevisionId =
        //   +otherRevisionsId?.[index] > currentRevisionId
        //     ? +otherRevisionsId?.[index]
        //     : currentRevisionId;
        if (paragraphId)
          paragraphData[paragraphPosition] = {
            type: 'paragraph--bp_image',
            id: paragraphId,
            meta: {
              target_revision_id: currentRevisionId,
            },
          };
      });

      const input = paragraphData?.length
        ? getEditInput(
            submittedData.name,
            entityId,
            {},
            {
              field_slider_detail: {
                data: paragraphData.filter((a) => a),
              },
            },
            locale
          )
        : undefined;

      const req =
        input &&
        getEditReq(submittedData.name, entityId, input, locale, position);
      req && bluePrintArray.push(req);
    }
  }
};

/**
 * FREE FULL SCREEN IMAGE Create/Edit
 */

export const createFullScreenFreeImage = async ({
  submittedData,
  createEntityFn,
  bluePrintArray,
  locale,
  position,
}) => {
  const img = submittedData.field_immagine_libera_full?.[0] || undefined;
  const alt = img
    ? submittedData?.alt_field_immagine_libera_full_0 || img.name
    : undefined;
  const _copy = img && new File([img], img.name);
  if (_copy?.preview) delete _copy.preview;
  const relationshipInput = {};
  if (_copy) {
    const res = await createEntityFn({
      langCode: locale,
      customHeaders: {
        'Content-Type': 'application/octet-stream',
        Accept: 'application/vnd.api+json',
        'Content-Disposition': `file; filename="${_copy.name}"`,
      },
      body: _copy,
      fieldName: 'field_immagine_libera_full',
      entity: 'paragraph',
      bundle: ParagraphEnum.FREE_IMAGE_FULL_SCREEN,
      useAxios: false,
    });
    if (res) {
      const imageId = res.id;
      relationshipInput.field_immagine_libera_full = {
        data: {
          type: 'file--file',
          id: imageId,
          meta: {
            alt: alt || img.name,
          },
        },
      };
    }
    const input = img
      ? getInput(
          ParagraphEnum.FREE_IMAGE_FULL_SCREEN,
          {},
          { ...relationshipInput },
          locale
        )
      : undefined;
    const req = getReq(
      ParagraphEnum.FREE_IMAGE_FULL_SCREEN,
      input,
      locale,
      position
    );
    bluePrintArray.push(req);
  }
};

export const editFullScreenFreeImage = async ({
  submittedData,
  oldData,
  createEntityFn,
  entityId,
  bluePrintArray,
  locale,
  position,
}) => {
  const oldComponent = oldData.find(
    (c) =>
      c.name === submittedData.name &&
      c?.revisionId === submittedData?.revisionId
  );
  const oldImg = oldComponent?.field_immagine_libera_full?.[0];
  const newImg = submittedData?.field_immagine_libera_full?.[0];
  const isImgEdit =
    !oldImg ||
    oldImg.name !== newImg.name ||
    oldImg.size !== newImg.size ||
    oldImg.type !== newImg.type;
  const oldAlt = oldComponent?.alt_field_immagine_libera_full_0;
  const newAlt = submittedData?.alt_field_immagine_libera_full_0;
  const isAltEdit = oldAlt !== newAlt;
  const relationshipInput = {};
  if (isImgEdit) {
    const img = submittedData.field_immagine_libera_full?.[0] || undefined;
    const alt = img
      ? submittedData?.alt_field_immagine_libera_full_0 || img.name
      : undefined;
    const _copy = img && new File([img], img.name);
    if (_copy?.preview) delete _copy.preview;
    if (_copy) {
      const res = await createEntityFn({
        langCode: locale,
        customHeaders: {
          'Content-Type': 'application/octet-stream',
          Accept: 'application/vnd.api+json',
          'Content-Disposition': `file; filename="${_copy.name}"`,
        },
        body: _copy,
        fieldName: 'field_immagine_libera_full',
        entity: 'paragraph',
        bundle: ParagraphEnum.FREE_IMAGE_FULL_SCREEN,
        useAxios: false,
      });
      if (res) {
        const imageId = res.id;
        relationshipInput.field_immagine_libera_full = {
          data: {
            type: 'file--file',
            id: imageId,
            meta: {
              alt: alt || img.name,
            },
          },
        };
      }
    }
  }

  if (!isImgEdit && isAltEdit) {
    const imgId = oldComponent?.oldImgId;
    relationshipInput.field_immagine_libera_full = {
      data: {
        type: 'file--file',
        id: imgId,
        meta: {
          alt: newAlt || oldAlt,
        },
      },
    };
  }

  if (oldImg || newImg) {
    const input = getEditInput(
      submittedData.name,
      entityId,
      {},
      { ...relationshipInput },
      locale
    );
    const req =
      input &&
      getEditReq(submittedData.name, entityId, input, locale, position);
    req && bluePrintArray.push(req);
  }
};

/**
 * TECHNICAL SHEET Create/Edit
 */
export const createTechnicalSheet = async ({
  submittedData,
  createEntityFn,
  bluePrintArray,
  locale,
  position,
}) => {
  const attributes = {};
  const relationships = {};
  Object.entries(submittedData).map(([k, v]) => {
    switch (k) {
      case 'field_modello_3d':
      case 'field_website':
      case 'field_collezione':
        return v
          ? (attributes[k] = {
              uri: v,
              title: submittedData[`alt_${k}_0`] || v,
            })
          : null;
      case 'field_anno':
        return v?.value ? (attributes[k] = parseInt(v.value)) : null;
      case 'field_descrizione':
      case 'field_dimensioni':
      case 'field_processo_produttivo':
        return v ? (attributes[k] = v) : null;
      case 'field_materiali':
      case 'field_caratteristiche':
        return v && v?.length
          ? (relationships[k] = {
              data: v.map((val) => ({
                type: `taxonomy_term--${k.split('_').pop()}`,
                id: val?.value,
              })),
            })
          : null;
      default:
        break;
    }
  });
  // È un immagine
  if (submittedData.field_sheet_image?.length) {
    const img = submittedData.field_sheet_image?.[0] || undefined;
    const alt = img
      ? submittedData?.alt_field_sheet_image_0 || img.name
      : undefined;
    const _copy = img && new File([img], img.name);
    if (_copy?.preview) delete _copy.preview;
    if (_copy) {
      const res = await createEntityFn({
        langCode: locale,
        customHeaders: {
          'Content-Type': 'application/octet-stream',
          Accept: 'application/vnd.api+json',
          'Content-Disposition': `file; filename="${_copy.name}"`,
        },
        body: _copy,
        fieldName: 'field_sheet_image',
        entity: 'paragraph',
        bundle: ParagraphEnum.TECHNICAL_SHEET,
        useAxios: false,
      });
      if (res) {
        const imageId = res.id;
        relationships.field_sheet_image = {
          data: {
            type: 'file--file',
            id: imageId,
            meta: {
              alt: alt || img.name,
            },
          },
        };
      }
    }
  }
  // Designer
  if (submittedData.field_designer) {
    if (submittedData.field_designer?.value) {
      // Il designer esiste ed uso questo
      relationships['field_designer'] = {
        data: {
          type: 'taxonomy_term--elenco_designer',
          id: submittedData.field_designer?.value,
        },
      };
    } else {
      // Creo il designer, aspetto l'id, e lo vado a mettere nelle relationship
      const input = {
        data: {
          type: 'taxonomy_term--elenco_designer',
          attributes: {
            name: submittedData.field_designer,
          },
        },
      };
      const res = await createEntityFn({
        useLoader: false,
        langCode: locale,
        body: input,
        entity: 'taxonomy_term',
        bundle: 'elenco_designer',
      });
      if (res) {
        const designerId = res.data?.id || undefined;
        const designerType = res.data?.type || undefined;
        if (designerId && designerType)
          relationships['field_designer'] = {
            data: {
              type: designerType,
              id: designerId,
            },
          };
      }
    }
  }
  const input =
    Object.keys(attributes)?.length || Object.keys(relationships)?.length
      ? getInput(
          ParagraphEnum.TECHNICAL_SHEET,
          attributes,
          relationships,
          locale
        )
      : undefined;
  const req = input
    ? getReq(ParagraphEnum.TECHNICAL_SHEET, input, locale, position)
    : undefined;
  req && bluePrintArray.push(req);
};

export const editTechnicalSheet = async ({
  submittedData,
  oldData,
  createEntityFn,
  entityId,
  bluePrintArray,
  locale,
  position,
}) => {
  const attributes = {};
  const relationships = {};
  const oldComponent = oldData.find(
    (c) =>
      c.name === submittedData.name &&
      c?.revisionId === submittedData?.revisionId
  );

  // Immagine
  const oldImg = oldComponent?.field_sheet_image?.[0];
  const newImg = submittedData?.field_sheet_image?.[0];
  const isImgEdit =
    !oldImg ||
    oldImg.name !== newImg.name ||
    oldImg.size !== newImg.size ||
    oldImg.type !== newImg.type;
  const oldAlt = oldComponent?.alt_field_sheet_image_0;
  const newAlt = submittedData?.alt_field_sheet_image_0;
  const isAltEdit = oldAlt !== newAlt;

  if (isImgEdit) {
    const img = submittedData.field_sheet_image?.[0] || undefined;
    const alt = img
      ? submittedData?.alt_field_sheet_image_0 || img.name
      : undefined;
    const _copy = img && new File([img], img.name);
    if (_copy?.preview) delete _copy.preview;
    if (_copy) {
      const res = await createEntityFn({
        langCode: locale,
        customHeaders: {
          'Content-Type': 'application/octet-stream',
          Accept: 'application/vnd.api+json',
          'Content-Disposition': `file; filename="${_copy.name}"`,
        },
        body: _copy,
        fieldName: 'field_sheet_image',
        entity: 'paragraph',
        bundle: ParagraphEnum.TECHNICAL_SHEET,
        useAxios: false,
      });
      if (res) {
        const imageId = res.id;
        relationships.field_sheet_image = {
          data: {
            type: 'file--file',
            id: imageId,
            meta: {
              alt: alt || img.name,
            },
          },
        };
      }
    }
  }

  if (!isImgEdit && isAltEdit) {
    const imgId = oldComponent?.oldImgId;
    relationships.field_sheet_image = {
      data: {
        type: 'file--file',
        id: imgId,
        meta: {
          alt: newAlt || oldAlt,
        },
      },
    };
  }

  // Designer
  const oldDesigner = oldComponent?.field_designer;
  const newDesigner = submittedData?.field_designer;
  const isDesignerEdit = newDesigner && oldDesigner !== newDesigner;
  if (isDesignerEdit) {
    if (submittedData.field_designer?.value) {
      // Il designer esiste ed uso questo
      relationships['field_designer'] = {
        data: {
          type: 'taxonomy_term--elenco_designer',
          id: submittedData.field_designer?.value,
        },
      };
    } else {
      // Creo il designer, aspetto l'id, e lo vado a mettere nelle relationship
      const input = {
        data: {
          type: 'taxonomy_term--elenco_designer',
          attributes: {
            name: submittedData.field_designer,
          },
        },
      };
      const res = await createEntityFn({
        useLoader: false,
        langCode: locale,
        body: input,
        entity: 'taxonomy_term',
        bundle: 'elenco_designer',
      });
      if (res) {
        const designerId = res.data?.id || undefined;
        const designerType = res.data?.type || undefined;
        if (designerId && designerType)
          relationships['field_designer'] = {
            data: {
              type: designerType,
              id: designerId,
            },
          };
      }
    }
  }

  const isEdit = (key, oldComponent, submittedData) => {
    const oldValue = oldComponent?.[key];
    const newValue = submittedData?.[key];
    const isEdit = oldValue !== newValue;
    return isEdit;
  };

  // Tutti gli altri campi
  Object.entries(submittedData).map(([k, v]) => {
    switch (k) {
      case 'field_modello_3d':
      case 'field_website':
      case 'field_collezione':
        return v
          ? (attributes[k] = {
              uri: isEdit(k, oldComponent, submittedData)
                ? v
                : oldComponent?.[k],
              title: isEdit(k, oldComponent, submittedData)
                ? submittedData[`alt_${k}_0`] || v
                : oldComponent[`alt_${k}_0`] || oldComponent?.[k],
            })
          : null;
      case 'field_anno':
        return v?.value
          ? (attributes[k] = parseInt(
              isEdit(k, oldComponent, submittedData)
                ? v.value
                : oldComponent?.[k]?.value || 0
            ))
          : null;
      case 'field_descrizione':
      case 'field_dimensioni':
      case 'field_processo_produttivo':
        return v
          ? (attributes[k] = isEdit(k, oldComponent, submittedData)
              ? v
              : oldComponent?.[k] || null)
          : null;
      case 'field_materiali':
      case 'field_caratteristiche':
        return v && v?.length
          ? (relationships[k] = {
              data: isEdit(k, oldComponent, submittedData)
                ? v.map((val) => ({
                    type: `taxonomy_term--${k.split('_').pop()}`,
                    id: val?.value,
                  }))
                : oldComponent?.[k].map((val) => ({
                    type: `taxonomy_term--${k.split('_').pop()}`,
                    id: val?.value,
                  })),
            })
          : null;
      default:
        break;
    }
  });

  const input =
    Object.keys(attributes)?.length || Object.keys(relationships)?.length
      ? getEditInput(
          submittedData.name,
          entityId,
          attributes,
          {
            ...relationships,
          },
          locale
        )
      : undefined;
  const req =
    input && getEditReq(submittedData.name, entityId, input, locale, position);
  req && bluePrintArray.push(req);
};
